import React, { useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import { useTranslation } from 'react-i18next';
import s from './styles.module.css';


import {
    useClientsWithoutWhatsapp,
    usePostClientWithoutWhatsapp,
} from '../../../../api/clients-without-whatsapp/hooks';
import ArrayIsEmpty from "../../../UI/Array-is-empty/Array-is-empty";
import ConfirmModal from "../confirm-modal";
import Pagination from "../../../UI/Pagination/Pagination";

const TableClients = ({ page, onPageChange }) => {
    const { t } = useTranslation();

    const { data, isLoading } = useClientsWithoutWhatsapp(page);
    const { mutate } = usePostClientWithoutWhatsapp();

    const [tableData, setTableData] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);

    useEffect(() => {
        if (data?.content) {
            setTableData(data.content);
        }
    }, [data]);

    const totalPages = data?.totalPages || 0;

    const columns = useMemo(
        () => [
            {
                Header: t('fullNameWithLastName'),
                accessor: 'fullName',
            },
            {
                Header: t('phoneNumber'),
                accessor: 'phone',
            },
            {
                Header: t('trackCode'),
                accessor: 'trackCode',
                Cell: ({ value }) =>
                    Array.isArray(value) && value.length
                        ? value.join(', ')
                        : '—',
            },
            {
                Header: t('status'),
                accessor: 'notified',
                Cell: ({ row }) => (
                    <input
                        type="checkbox"
                        checked={row.original.notified}
                        disabled={row.original.notified}
                        onChange={() =>
                            setSelectedClient(row.original)
                        }
                    />
                ),
            },
        ],
        [t],
    );

    const tableInstance = useTable({
        columns,
        data: tableData,
    });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = tableInstance;

    const handleConfirm = () => {
        if (!selectedClient) return;

        mutate(selectedClient.phone, {
            onSuccess: () => {
                setSelectedClient(null);
            },
        });
    };

    if (isLoading) {
        return <ArrayIsEmpty textOfEmpty={t('loading')} />;
    }

    return (
        <>
            <div className={s['table-container']}>
                <div className={s['table-order']}>
                    {tableData.length === 0 ? (
                        <ArrayIsEmpty textOfEmpty={t('noDataToShow')} />
                    ) : (
                        <table className={s.table} {...getTableProps()}>
                            <thead>
                            {headerGroups.map((hg) => (
                                <tr {...hg.getHeaderGroupProps()}>
                                    {hg.headers.map((col) => (
                                        <th
                                            {...col.getHeaderProps()}
                                            className={s['th-style']}
                                        >
                                            {col.render('Header')}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>

                            <tbody {...getTableBodyProps()}>
                            {rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr
                                        {...row.getRowProps()}
                                        className={s.tbody}
                                    >
                                        {row.cells.map((cell) => (
                                            <td
                                                {...cell.getCellProps()}
                                                className={s['td-style']}
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    )}
                </div>

                {totalPages > 1 && (
                    <div className={s['div-pages']}>
                        <Pagination
                            totalPages={totalPages}
                            currentPage={page}
                            setPage={onPageChange}
                        />
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={!!selectedClient}
                text={t('confirmContact')}
                onClose={() => setSelectedClient(null)}
                onConfirm={handleConfirm}
            />
        </>
    );
};

export default TableClients;
